/**
 * 音频处理组合式函数
 * 职责：
 * 1. 封装微信录音API
 * 2. 封装Howler.js音频播放
 * 3. 不维护状态，状态由chatStore统一管理
 */

import { Howl, Howler } from 'howler'
import { apiService } from '@/api/apiService.js'

// ==================== Howler.js 状态 ====================

/** @type {Howl|null} 当前播放器实例 */
let currentHowl = null

/** @type {boolean} Howler是否已初始化 */
let isHowlerInitialized = false

// ==================== 微信语音播放状态 ====================

/** @type {string|null} 当前播放的微信录音 localId */
let currentWechatVoiceLocalId = null

/** @type {Function|null} 当前微信语音播放结束事件监听器 */
let currentWechatVoiceEndHandler = null

// ==================== 常量配置 ====================

/** 播放超时时间（毫秒） */
const PLAY_TIMEOUT = 10000

/** 录音启动超时时间（毫秒） */
const RECORD_START_TIMEOUT = 5000

/** 录音停止超时时间（毫秒） */
const RECORD_STOP_TIMEOUT = 10000

// ==================== Howler.js 初始化 ====================

/**
 * 初始化 Howler.js 全局配置
 */
function initializeHowler() {
  if (isHowlerInitialized) return

  try {
    Howler.autoUnlock = true      // 自动解锁音频上下文
    Howler.html5PoolSize = 10     // HTML5 音频池大小
    Howler.autoSuspend = false    // 不自动暂停音频上下文
    Howler.volume(1.0)            // 设置音量

    // Howler.js 初始化完成

    isHowlerInitialized = true
  } catch (error) {
    // error('🎵 Howler.js 初始化失败:', error)
  }
}

// 自动初始化
initializeHowler()

// ==================== 音频播放（Howler.js） ====================

/**
 * 播放音频 - 使用 Howler.js
 * @param {string} audioUrl - 音频URL
 * @param {Object} callbacks - 回调函数
 * @param {Function} [callbacks.onPlay] - 播放开始回调
 * @param {Function} [callbacks.onEnded] - 播放结束回调
 * @param {Function} [callbacks.onError] - 播放错误回调
 * @param {Function} [callbacks.onNeedUserGesture] - 需要用户手势回调
 * @returns {Promise<Howl>} Howl实例
 */
export async function playAudio(audioUrl, callbacks = {}) {
  // debug('🎵 开始播放音频:', audioUrl)

  // 先停止当前播放
  stopAudio()

  return new Promise((resolve, reject) => {
    let isResolved = false
    let timeoutId = null
    const playStartTime = Date.now()

    /**
     * 清理资源并完成 Promise
     * @param {Function} finalizer - resolve 或 reject
     * @param {any} [value] - 传递的值或错误
     */
    const cleanup = (finalizer, value) => {
      if (isResolved) return
      isResolved = true
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      finalizer(value)
    }

    // 创建 Howl 实例
    currentHowl = new Howl({
      src: [audioUrl],
      html5: true,
      preload: true,
      volume: 1.0,

      // 播放开始
      onplay: () => {
        callbacks.onPlay?.()
        cleanup(resolve, currentHowl)
      },

      // 播放结束
      onend: () => {
        // debug('🎵 音频播放结束')
        callbacks.onEnded?.()
        // 自动清理资源
        if (currentHowl) {
          currentHowl.unload()
          currentHowl = null
        }
        cleanup(resolve)
      },

      // 播放错误
      onplayerror: (id, error) => {
        // error('🎵 音频播放错误:', error)
        callbacks.onError?.(error)

        // 清理资源
        if (currentHowl) {
          currentHowl.unload()
          currentHowl = null
        }

        // 检查是否是权限错误
        if (error && (error.message?.includes('play') || error.code === 0)) {
          // debug('🎵 可能是权限错误，触发用户手势回调')
          setTimeout(() => callbacks.onNeedUserGesture?.(), 50)
          cleanup(resolve) // 权限错误不抛出异常
        } else {
          cleanup(reject, error)
        }
      },

      // 加载错误
      onloaderror: (id, error) => {
        // error('🎵 音频加载错误:', error)
        callbacks.onError?.(error)

        // 清理资源
        if (currentHowl) {
          currentHowl.unload()
          currentHowl = null
        }

        cleanup(reject, new Error(`音频加载失败: ${error}`))
      },

      // 音频加载完成
      onload: () => {
        // 加载完成
      }
    })

    // 尝试播放
    try {
      currentHowl.play()
    } catch (error) {
      // error('🎵 播放调用失败:', error)
      if (currentHowl) {
        currentHowl.unload()
        currentHowl = null
      }
      cleanup(reject, error)
    }

    // 设置超时
    timeoutId = setTimeout(() => {
      // warn('🎵 音频播放超时')
      if (currentHowl) {
        currentHowl.unload()
        currentHowl = null
      }
      cleanup(reject, new Error('音频播放超时'))
    }, PLAY_TIMEOUT)
  })
}

/**
 * 停止音频播放
 * @returns {void}
 */
export function stopAudio() {
  if (currentHowl) {
    try {
      // debug('🎵 停止音频播放')
      currentHowl.stop()
      currentHowl.unload()
      currentHowl = null
    } catch (error) {
      // warn('🎵 停止音频时出错:', error)
      currentHowl = null
    }
  }
}

/**
 * 检查是否正在播放
 * @returns {boolean} 是否正在播放
 */
export function isPlaying() {
  return currentHowl !== null && currentHowl.playing()
}

/**
 * 尝试恢复音频上下文
 * @returns {Promise<boolean>} 是否成功
 */
export async function resumeAudioContext() {
  try {
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      await Howler.ctx.resume()
      // debug('🎵 音频上下文已恢复')
      return true
    }
    return false
  } catch (error) {
    // warn('🎵 恢复音频上下文失败:', error)
    return false
  }
}

/**
 * 解锁音频（播放静音片段以解锁自动播放限制）
 * 必须在用户交互事件（点击/触摸）中同步调用
 */
export function unlockAudio() {
  // debug('🔓 尝试解锁音频上下文...')

  // 1. 恢复上下文
  resumeAudioContext()

  // 2. 播放一个极短的静音片段
  // 这比单纯 resume 更可靠，尤其是在 iOS Safari/WeChat 中
  const silentHowl = new Howl({
    src: ['data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAGZGF0YQQAAAAAAA=='], // 极短的静音WAV
    html5: false, // 必须使用 Web Audio API
    volume: 0.1,
    onend: () => {
      // debug('🔓 静音片段播放结束，音频应已解锁')
      silentHowl.unload()
    },
    onplayerror: (id, error) => {
      // warn('🔓 解锁音频失败:', error)
      silentHowl.unload()
    }
  })

  silentHowl.play()
}

// ==================== 微信录音 ====================

/**
 * 开始微信录音
 * @returns {Promise<void>}
 * @throws {Error} 微信SDK未加载或录音失败
 */
export async function startWechatRecording() {
  if (!window.wx) {
    const error = '微信SDK未加载'
    // error('❌', error)
    throw new Error(error)
  }

  // 授权守卫：SDK 未就绪（config/ready 未完成或失败）时直接拒绝，
  // 避免 wx.startRecord 在未授权状态下被调用而出现"假录音"（UI 显示录音中，底层未真正录音）
  // 动态导入避免与 wechat.js 形成循环依赖（wechat.js 顶层导入了本模块的 unlockAudio）
  const { isWechatReady } = await import('@/utils/wechat.js')
  if (!isWechatReady()) {
    throw new Error('微信授权未完成，请刷新页面后重试')
  }

  // info('🎤 开始微信录音...')

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      // error('❌ 微信录音启动超时')
      reject(new Error('微信录音启动超时'))
    }, RECORD_START_TIMEOUT)

    window.wx.startRecord({
      success: () => {
        clearTimeout(timeout)
        // info('✅ 微信录音开始成功')
        resolve()
      },
      fail: (res) => {
        clearTimeout(timeout)
        // error('❌ 微信录音失败:', res.errMsg)
        // error('❌ 完整错误信息:', JSON.stringify(res))

        let errorMessage = '录音失败，请重试'
        if (res.errMsg) {
          if (res.errMsg.includes('invalid signature')) {
            errorMessage = '微信配置验证失败，请刷新页面重试'
            // error('❌ 签名验证失败，请检查微信配置')
          } else if (res.errMsg.includes('permission')) {
            errorMessage = '录音权限被拒绝，请在微信设置中允许录音权限'
            // error('❌ 录音权限被拒绝')
          } else if (res.errMsg.includes('busy')) {
            errorMessage = '录音设备忙碌，请稍后重试'
            // error('❌ 录音设备忙碌')
          } else if (res.errMsg.includes('not allow')) {
            errorMessage = '微信不允许录音，请检查权限配置'
            // error('❌ 微信不允许录音，可能是jsapi配置问题')
          }
        }
        reject(new Error(errorMessage))
      }
    })
  })
}

/**
 * 停止微信录音并获取音频数据（新流程）
 * @param {string} sessionId - 会话ID（目前仅用于日志，兼容历史签名）
 * @returns {Promise<{type: string, localId: string, serverId: string, fileBlob: Blob, duration: number}>}
 * @throws {Error} 微信SDK未加载或录音失败
 */
export async function stopWechatRecording(sessionId) {
  if (!window.wx) {
    throw new Error('微信SDK未加载')
  }

  // debug('🎤 停止微信录音')

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('微信停止录音超时'))
    }, RECORD_STOP_TIMEOUT)

    window.wx.stopRecord({
      success: async (res) => {
        clearTimeout(timeout)
        // info('🎤 微信录音停止成功:', res.localId)

        try {
          // 上传并获取音频文件Blob（新流程）
          const { serverId, fileBlob } = await uploadWechatVoiceForFileBlob(res.localId, sessionId)

          resolve({
            type: 'wechat',
            localId: res.localId,
            serverId,
            fileBlob,
            duration: 0 // 微信不提供时长
          })
        } catch (error) {
          // error('🎤 处理微信录音失败:', error)
          reject(error)
        }
      },
      fail: (res) => {
        clearTimeout(timeout)
        // error('🎤 微信停止录音失败:', res)
        reject(new Error(`停止录音失败: ${res.errMsg || '未知错误'}`))
      }
    })
  })
}

/**
 * 上传微信录音并获取音频文件Blob（新流程）
 *
 * 注意：这里分两步：
 * 1）调用 wx.uploadVoice 上传到微信服务器，得到 serverId(mediaId)
 * 2）调用后端「获取音频流」接口 getWechatAudioUrl(mediaId) 拿到 Blob
 *
 * uploadWechatAudio 仍按原设计返回“文件名”，保留给其他场景使用，
 * 这里获取 Blob 要走 getWechatAudioUrl 这一条链路。
 *
 * @param {string} localId - 微信录音本地ID
 * @param {string} sessionId - 会话ID（已废弃，保留参数兼容性）
 * @returns {Promise<{serverId: string, fileBlob: Blob}>}
 * @throws {Error} 上传失败
 * @private
 */
async function uploadWechatVoiceForFileBlob(localId, sessionId) {
  // 1. 上传到微信服务器获取serverId（mediaId）
  const serverId = await new Promise((resolve, reject) => {
    window.wx.uploadVoice({
      localId: localId,
      isShowProgressTips: 1,
      success: (res) => {
        // info('✅ 微信录音上传成功，serverId:', res.serverId)
        resolve(res.serverId)
      },
      fail: (res) => {
        // error('❌ 微信录音上传失败:', res)
        reject(new Error(`上传失败: ${res.errMsg || '未知错误'}`))
      }
    })
  })

  // 2. 通过后端「获取音频流」接口拿到音频二进制流
  const fileBlob = await apiService.getWechatAudioUrl(serverId)

  if (!(fileBlob instanceof Blob)) {
    throw new Error('后端返回的音频数据格式错误')
  }

  return { serverId, fileBlob }
}

/**
 * 上传微信录音并获取音频流（旧流程，保留兼容）
 * @param {string} localId - 微信录音本地ID
 * @returns {Promise<Blob>}
 * @throws {Error} 上传失败
 * @private
 */
async function uploadWechatVoice(localId) {
  // debug('🎤 上传微信录音（旧流程）:', localId)

  // 1. 上传到微信服务器获取serverId
  const serverId = await new Promise((resolve, reject) => {
    window.wx.uploadVoice({
      localId: localId,
      isShowProgressTips: 1,
      success: (res) => {
        // info('✅ 微信录音上传成功，serverId:', res.serverId)
        resolve(res.serverId)
      },
      fail: (res) => {
        // error('❌ 微信录音上传失败:', res)
        reject(new Error(`上传失败: ${res.errMsg || '未知错误'}`))
      }
    })
  })

  // 2. 通过后端获取音频二进制流
  // debug('🔄 通过后端获取音频流...')
  const audioBlob = await apiService.getWechatAudioUrl(serverId)

  if (!(audioBlob instanceof Blob)) {
    throw new Error('后端返回的音频数据格式错误')
  }

  // info('✅ 获取音频流成功，大小:', audioBlob.size, 'bytes')
  return audioBlob
}

/**
 * 播放微信录音
 * @param {string} localId - 微信录音本地ID
 * @param {Object} [callbacks={}] - 回调函数
 * @param {Function} [callbacks.onPlay] - 播放开始回调
 * @param {Function} [callbacks.onEnded] - 播放结束回调
 * @param {Function} [callbacks.onError] - 播放错误回调
 * @returns {Promise<void>}
 * @throws {Error} 微信SDK未加载或播放失败
 */
export async function playWechatVoice(localId, callbacks = {}) {
  if (!window.wx) {
    throw new Error('微信SDK未加载')
  }

  // debug('🎵 播放微信录音:', localId)

  // 如果已经在播放其他录音，先停止
  if (currentWechatVoiceLocalId && currentWechatVoiceLocalId !== localId) {
    stopWechatVoice(currentWechatVoiceLocalId)
  }

  return new Promise((resolve, reject) => {
    let isResolved = false

    // 清理函数
    const cleanup = () => {
      if (currentWechatVoiceEndHandler) {
        window.wx.offVoicePlayEnd(currentWechatVoiceEndHandler)
        currentWechatVoiceEndHandler = null
      }
      if (currentWechatVoiceLocalId === localId) {
        currentWechatVoiceLocalId = null
      }
    }

    // 播放结束事件监听器
    const onVoicePlayEndHandler = (res) => {
      // 只处理当前播放的录音
      if (res.localId === localId && !isResolved) {
        // debug('🎵 微信录音播放结束:', res.localId)
        isResolved = true
        cleanup()
        // 调用回调
        callbacks.onEnded?.()
        resolve()
      }
    }

    // 保存当前播放状态
    currentWechatVoiceLocalId = localId
    currentWechatVoiceEndHandler = onVoicePlayEndHandler

    // 注册播放结束事件监听器
    window.wx.onVoicePlayEnd(onVoicePlayEndHandler)

    // 开始播放
    window.wx.playVoice({
      localId,
      success: () => {
        // debug('🎵 微信录音播放成功')
        callbacks.onPlay?.()
        // 注意：这里不 resolve，等待播放完成事件
      },
      fail: (error) => {
        // error('🎵 微信录音播放失败:', error)
        isResolved = true
        cleanup()
        callbacks.onError?.(error)
        reject(error)
      }
    })
  })
}

/**
 * 停止微信录音播放
 * @param {string} localId - 微信录音本地ID
 * @returns {void}
 */
export function stopWechatVoice(localId) {
  if (!window.wx) return

  try {
    window.wx.stopVoice({ localId })
    // debug('🎵 微信录音播放已停止')

    // 清理事件监听器
    if (currentWechatVoiceEndHandler && currentWechatVoiceLocalId === localId) {
      window.wx.offVoicePlayEnd(currentWechatVoiceEndHandler)
      currentWechatVoiceEndHandler = null
      currentWechatVoiceLocalId = null
    }
  } catch (error) {
    // warn('🎵 停止微信录音播放出错:', error)
  }
}

/**
 * 清理所有音频资源
 * @returns {void}
 */
export function cleanupAudio() {
  stopAudio()
}

